"use client";

/**
 * Le delai de debit du paiement differe, en heures (option OPT-PAY-LATER-HOURS-BEFORE).
 *
 * POURQUOI CE HOOK EXISTE. Ce nombre est ecrit dans des phrases que le client lit et
 * sur lesquelles il s'engage — « votre carte sera debitee 72 h avant la visite »,
 * le mandat de la page cartes. Il etait recopie en dur dans les sept fichiers de
 * langue, alors que le serveur, lui, suit l'option : la baisser a 1 pour tester, et
 * l'interface continuait d'annoncer 72 h a tout le monde. Un mandat de prelevement
 * qui annonce le mauvais delai n'est pas une coquille d'affichage.
 *
 * `null` = valeur inconnue, et c'est un etat NORMAL, pas une panne : ligne absente,
 * valeur non numerique, reseau coupe. Les appelants ont alors une formulation sans
 * chiffre (« avant le debut de la visite »). Jamais de repli sur 72 : mieux vaut en
 * dire moins que d'annoncer un delai faux.
 *
 * Meme cache que `usePayLaterOpenToAllPro` — une seule requete pour la duree de la
 * page, les echecs ne sont pas mis en cache.
 *
 * CE N'EST QU'UN AFFICHAGE. Le delai qui fait foi est celui que le serveur lit au
 * moment de calculer l'echeance ; une echeance deja creee garde le sien, fige. Une
 * page qui parle d'une reservation DEJA prise doit donc deduire le delai de son
 * echeance (tourStartAt - dueAt), pas d'ici.
 */
import { useEffect, useState } from "react";

const OPTION_UUID = "OPT-PAY-LATER-HOURS-BEFORE";

let cached: number | null | undefined;
let inFlight: Promise<number | null> | null = null;

function readOption(): Promise<number | null> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  const base = process.env.NEXT_PUBLIC_SOCKET || "";
  if (!base) return Promise.resolve(null);

  inFlight = fetch(`${base}/api/options/uuid/${encodeURIComponent(OPTION_UUID)}`, {
    headers: { Accept: "application/json" },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      // Meme lecture que `settings.getInt` cote serveur : seul un entier
      // strictement positif compte. Une case vide, un 0 ou un texte valent
      // « inconnu » ici, la ou le serveur retombe sur son defaut.
      const raw = Number(String(data?.value ?? "").trim());
      cached = Number.isFinite(raw) && raw > 0 ? Math.round(raw) : null;
      inFlight = null;
      return cached;
    })
    .catch(() => {
      inFlight = null;
      return null;
    });

  return inFlight;
}

export function usePayLaterHoursBefore(): number | null {
  const [hours, setHours] = useState<number | null>(cached ?? null);

  useEffect(() => {
    let alive = true;
    readOption().then((value) => {
      if (alive) setHours(value);
    });
    return () => {
      alive = false;
    };
  }, []);

  return hours;
}

export default usePayLaterHoursBefore;
